// Servicio de caché LRU (Least Recently Used) para optimizar el rendimiento del dashboard
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live en milisegundos
  lastAccessed: number; // Timestamp de último acceso para LRU
}

class CacheService {
  private cache = new Map<string, CacheEntry<unknown>>();
  
  // TTL por defecto: 5 minutos
  private defaultTTL = 5 * 60 * 1000;
  
  // Límite máximo de entradas en el cache (LRU)
  private maxSize = 500;

  set<T>(key: string, data: T, ttl?: number): void {
    // Si el cache está lleno, eliminar la entrada menos recientemente usada (LRU)
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
      lastAccessed: Date.now()
    };
    this.cache.set(key, entry);
  }
  
  // Eliminar la entrada menos recientemente usada
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestAccess) {
        oldestAccess = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Verificar si el cache ha expirado
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Actualizar timestamp de último acceso para LRU
    entry.lastAccessed = Date.now();

    return entry.data as T;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }

    // Verificar si el cache ha expirado
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  // Eliminar claves que coincidan con un patrón
  deletePattern(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }

  // Limpiar entradas expiradas
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  // Obtener estadísticas del caché
  getStats(): { size: number; maxSize: number; keys: string[]; hits: number; misses: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      keys: Array.from(this.cache.keys()),
      hits: 0, // Podría implementarse con contadores si se necesita
      misses: 0
    };
  }
  
  // Establecer el tamaño máximo del cache
  setMaxSize(size: number): void {
    this.maxSize = size;
    // Si el cache actual es más grande que el nuevo límite, eliminar entradas LRU
    while (this.cache.size > this.maxSize) {
      this.evictLRU();
    }
  }
}

// Instancia singleton del servicio de caché
export const cacheService = new CacheService();

// Limpiar caché expirado cada 10 minutos
setInterval(() => {
  cacheService.cleanup();
}, 10 * 60 * 1000);
